import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/ClientLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DollarSign, Briefcase, Users, FileText, TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';

const ReportsPage = () => {
  const { token, API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState(null);
  const [jobsData, setJobsData] = useState(null);
  const [crewsData, setCrewsData] = useState(null);
  const [estimatesData, setEstimatesData] = useState(null);
  const [outstandingData, setOutstandingData] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [revenue, jobs, crews, estimates, outstanding] = await Promise.all([
        axios.get(`${API}/reports/revenue`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/jobs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/crews`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/estimates`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/reports/outstanding`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRevenueData(revenue.data);
      setJobsData(jobs.data);
      setCrewsData(crews.data);
      setEstimatesData(estimates.data);
      setOutstandingData(outstanding.data);
    } catch (error) {
      console.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const jobStatusData = jobsData ? [
    { name: 'Completed', value: jobsData.completed, color: '#10B981' },
    { name: 'In Progress', value: jobsData.in_progress, color: '#F59E0B' },
    { name: 'Scheduled', value: jobsData.scheduled, color: '#3B82F6' },
    { name: 'Cancelled', value: jobsData.cancelled, color: '#EF4444' }
  ].filter(d => d.value > 0) : [];

  const estimateStatusData = estimatesData ? [
    { name: 'Approved', value: estimatesData.approved + estimatesData.converted, color: '#10B981' },
    { name: 'Pending', value: estimatesData.pending, color: '#F59E0B' },
    { name: 'Declined', value: estimatesData.declined, color: '#EF4444' }
  ].filter(d => d.value > 0) : [];

  return (
    <ClientLayout title="Reports">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-gray-200" data-testid="report-revenue">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${(revenueData?.total_revenue || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400">{revenueData?.invoice_count || 0} invoices paid</p>
                  </div>
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200" data-testid="report-jobs">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Jobs Completed</p>
                    <p className="text-2xl font-bold text-gray-900">{jobsData?.completed || 0}</p>
                    <p className="text-xs text-emerald-600 flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      {jobsData?.completion_rate?.toFixed(1) || 0}% completion rate
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200" data-testid="report-estimates">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Estimate Conversion</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {estimatesData?.conversion_rate?.toFixed(1) || 0}%
                    </p>
                    <p className="text-xs text-gray-400">{estimatesData?.total || 0} total estimates</p>
                  </div>
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200" data-testid="report-outstanding">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Outstanding</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${(outstandingData?.total_outstanding || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-amber-600">{outstandingData?.invoice_count || 0} unpaid invoices</p>
                  </div>
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <TrendingDown className="h-5 w-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg">Job Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {jobStatusData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={jobStatusData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {jobStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-gray-500">No job data</div>
                )}
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg">Estimate Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {estimateStatusData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={estimateStatusData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {estimateStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-gray-500">No estimate data</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Crew Productivity */}
          <Card className="border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg">Crew Productivity</CardTitle>
            </CardHeader>
            <CardContent>
              {crewsData?.crews && crewsData.crews.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Crew</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Jobs Completed</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Revenue Generated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crewsData.crews.map((crew) => (
                        <tr key={crew.crew_id} className="border-b border-gray-100">
                          <td className="py-3 px-4 font-medium text-gray-900">{crew.crew_name}</td>
                          <td className="py-3 px-4 text-gray-600">{crew.jobs_completed}</td>
                          <td className="py-3 px-4 text-gray-900 font-medium">${crew.revenue_generated.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No crew productivity data</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </ClientLayout>
  );
};

export default ReportsPage;
